import { createContext, useContext } from "react"

import { cn } from "@/lib/utils"

/**
 * Marks the subtree rendered inside `AccountLayout`'s scrolling content column.
 * Only there does a sticky bar have a scroll container to pin against and a
 * known horizontal padding to bleed into.
 */
const AccountFormColumn = createContext(false)

/** Whether this subtree is rendered inside `AccountLayout`'s form column. */
export function useIsInAccountFormColumn() {
  return useContext(AccountFormColumn)
}

export function AccountFormColumnProvider({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <AccountFormColumn.Provider value={true}>
      {children}
    </AccountFormColumn.Provider>
  )
}

/**
 * Keeps a form's submit button reachable while the fields above it scroll.
 *
 * The forms using this are also rendered outside the account layout — the embed
 * signup and login dialogs, `ChangeEmailDialog`, and the embed verification
 * route — where there is no scroll column and no matching padding. The sticky
 * treatment is therefore opt-in: without the provider this is plain flow, which
 * is what those callers had before.
 *
 * On desktop the bar drops its border and bleed but stays sticky: the column
 * normally fits the viewport, but a short screen or a few validation errors can
 * still push it past the fold, and the submit button must stay on screen.
 *
 * The `-mx-6` bleed mirrors the column's `px-6` in `AccountLayout`. The two have
 * to move together, or the bar overflows its container horizontally.
 */
export function StickyFormActions({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  const isInFormColumn = useIsInAccountFormColumn()

  if (!isInFormColumn) {
    return (
      <div className={cn("flex flex-col gap-4", className)}>{children}</div>
    )
  }

  return (
    <div
      className={cn(
        "bg-background sticky bottom-0 -mx-6 mt-auto flex flex-col gap-4 border-t border-neutral-200 px-6 pt-4 pb-3",
        "lg:mx-0 lg:mt-0 lg:border-0 lg:px-0 lg:pt-0 lg:pb-0",
        className,
      )}
    >
      {children}
    </div>
  )
}
